// Which version of the redesigned pages a browser sees: "new" or "classic".
//
// The redesign (My Courses and the course page) is built beside the current
// pages rather than over them, so it can sit on staging — or even ship — without
// replacing anything. Turning it off is this switch, not a revert.
//
// Order of precedence:
//   1. `?ui=new` or `?ui=classic` on any URL: sets the choice and remembers it.
//   2. The remembered choice (localStorage), so testers keep the one they picked.
//   3. VITE_UI_DEFAULT from the build's .env file ("new" on staging and, since
//      October 2026, production); else "classic". A browser that chose classic
//      keeps it; going back to classic for everyone is that line, not a revert.
//
// Read once at page load: the router resolves a page's component on first visit,
// so changing the choice reloads the page (see setUiVersion).

const KEY = "rme-ui-version";
const VALID = ["new", "classic"];

const remembered = () => {
  try {
    return localStorage.getItem(KEY);
  } catch (_) {
    return null; // storage blocked (private mode, embedded)
  }
};

const remember = (value) => {
  try {
    localStorage.setItem(KEY, value);
  } catch (_) {
    /* the choice just won't persist */
  }
};

const fromQuery = () => {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("ui");
  return VALID.includes(value) ? value : null;
};

const buildDefault = () => {
  const value = import.meta.env.VITE_UI_DEFAULT;
  return VALID.includes(value) ? value : "classic";
};

let resolved = null;

export function uiVersion() {
  if (resolved) return resolved;
  const query = fromQuery();
  if (query) remember(query);
  const stored = remembered();
  resolved = query || (VALID.includes(stored) ? stored : null) || buildDefault();
  return resolved;
}

export const isNewUi = () => uiVersion() === "new";

/**
 * Whether to offer a way back to the redesign from a classic page: where it is
 * the build's default (staging), or where this browser has chosen a version
 * before (it was offered, or someone used ?ui=). Production customers who never
 * opted in see nothing.
 */
export const isRedesignOffered = () => buildDefault() === "new" || VALID.includes(remembered());

/** Switch and reload, keeping the current path (minus any ?ui=). */
export function setUiVersion(value) {
  if (!VALID.includes(value)) return;
  remember(value);
  const url = new URL(window.location.href);
  url.searchParams.delete("ui");
  window.location.assign(url.toString());
}
